import { CourtroomSession } from '@ftr/contracts/regional-api'
import { LocalTimeRange, Uuid } from '@ftr/contracts/type/shared'
import { InlineSaveStatus } from '@ftr/inline-forms'
import { LocalDateTime, LocalTime } from '@js-joda/core'
import { RealTimeSttMarkerEditSource } from './real-time-stt-marker'
import { RealTimeSttTranscriptElementBase } from './real-time-stt-transcript-element-base'

export const ALL_SESSION_MARKER_TYPES = ['sessionStart', 'sessionEnd', 'futureSessionStart'] as const
export type SessionMarkerType = (typeof ALL_SESSION_MARKER_TYPES)[number]

export interface RealTimeSttSessionMarker extends RealTimeSttTranscriptElementBase {
  type: 'SessionMarker'
  dividerType: SessionMarkerType
  sessionTimes: LocalTimeRange
  sessionNumber: number
  status: InlineSaveStatus<RealTimeSttSessionMarker>
  showAsOngoing: boolean
  source?: RealTimeSttMarkerEditSource
  forceResetFormKey?: string
  courtroomSessionId: Uuid
  departmentId: Uuid | undefined
}

export function getSessionMarkersFromCourtroomSessions(
  courtroomSessions: CourtroomSession[],
): RealTimeSttSessionMarker[] {
  return courtroomSessions.flatMap((courtroomSession, index) => {
    const sessionNumber = index + 1
    const endTime =
      courtroomSession.endTime ?? LocalDateTime.of(courtroomSession.startTime.toLocalDate(), LocalTime.MAX.withNano(0))
    const sessionTimes = new LocalTimeRange(courtroomSession.startTime.toLocalTime(), endTime.toLocalTime())

    return [
      toSessionMarker(
        courtroomSession.id,
        courtroomSession.departmentId,
        LocalDateTime.from(courtroomSession.startTime),
        'sessionStart',
        sessionTimes,
        sessionNumber,
      ),
      toSessionMarker(
        courtroomSession.id,
        courtroomSession.departmentId,
        LocalDateTime.from(endTime),
        'sessionEnd',
        sessionTimes,
        sessionNumber,
      ),
    ]
  })
}

export function getSessionMarkerId(courtroomSessionId: Uuid, markerType: SessionMarkerType): string {
  return `marker-${markerType}-${courtroomSessionId}`
}

function toSessionMarker(
  courtroomSessionId: Uuid,
  departmentId: Uuid | undefined,
  startTime: LocalDateTime,
  dividerType: SessionMarkerType,
  sessionTimes: LocalTimeRange,
  sessionNumber: number,
): RealTimeSttSessionMarker {
  return {
    type: 'SessionMarker' as const,
    dividerType,
    id: getSessionMarkerId(courtroomSessionId, dividerType),
    //These are the same because this marker is only used to indicate the start/end of a session
    startTime,
    endTime: startTime,
    sessionTimes,
    sessionNumber,
    status: 'loaded',
    showAsOngoing: false,
    courtroomSessionId,
    departmentId,
  }
}
